/**
 * specModesCore.js — чистая математика вкладки «Спец. режимы». [12.0.57]
 *
 * ПОВОД. Владелец: «проверь раздел „Спец. режимы“, кликни каждую кнопку». На
 * стенде с настоящим Cornerstone 5.6.3 (tests/стенды/specrezhimy) половина
 * вкладки отвечала числами, которых нет: «Умный W/L: мягкие ткани (медиана
 * NaN HU) → WL NaN» при щелчке в лёгкое, «ROI W/L: WW=NaN / WL=NaN», DSA —
 * «не удалось прочитать данные объёма». Причина одна: значения снимка брались
 * функцией uiManager._getVolumeScalarData, которая в окнах MPR на Cornerstone
 * 5.6 всегда пуста (у актёров нет скаляров, а window.cornerstone программа не
 * заводит), а точка щелчка пересчитывалась в воксель по прямоугольнику ВСЕЙ
 * области окон — в раскладке из пяти окон это чужое место чужого окна.
 *
 * Здесь — то, что не зависит от движка: значение в точке мира, проба вокруг
 * точки В ПЛОСКОСТИ ВИДА, срез, который сейчас на экране (с ROI или целиком),
 * класс ткани, окно по области, вычитание с MIP, геометрия ролика, шаг сетки,
 * соседняя закладка. Движок (точка щелчка → мир, кэш томов, окна) — в
 * ui/specModes.js. Тест (tests/specrezhimy_test.mjs) исполняет эти функции на
 * числах с заранее известным ответом.
 *
 * Данные тома — как у core/volumePick.данныеТома: {scalarData, dims, spacing,
 * origin, direction}; direction — три оси подряд (i, j, k), как в Cornerstone.
 * Снимок стопки — тот же вид с dims [ширина, высота, 1]. Вместо scalarData
 * можно дать получить(линейныйИндекс): у потокового тома Cornerstone цельного
 * массива нет, и getCompleteScalarDataArray собирает копию всего тома
 * (на 512×512×800 — 400 МБ) ради одной пробы 7×7. Читаем по вокселю.
 *
 * EXPORTS: осиВида, значениеВТочке, пробаВПлоскости, отсортировать, перцентиль,
 *   медиана, статистика, гистограмма, тканьПоHU, окноПоОбласти, срезВида,
 *   фигураВПлоскости, вФигуре, наПлоскости, мипРазности, геометрияСовпадает,
 *   планРолика, масштабСетки, соседняяЗакладка
 */
import { мирВИндекс, индексВМир } from './volumePick.js';

const скал = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const вект = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const норм = (v) => { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; };
const D = (данные) => (данные.direction && данные.direction.length === 9 ? данные.direction : [1, 0, 0, 0, 1, 0, 0, 0, 1]);
/** Значение по линейному индексу: из массива или через получить(). */
const читать = (данные, i) => (данные.scalarData ? данные.scalarData[i] : данные.получить(i));

/**
 * Оси плоскости вида: «вправо» и «вверх», единичные и перпендикулярные
 * нормали. Знак для проб и гистограмм не важен — квадрат вокруг точки
 * симметричен, а фигура ROI переводится в те же оси.
 */
export function осиВида(нормаль, верх) {
  const n = норм(нормаль);
  let вправо = вект(верх || [0, 1, 0], n);
  if (Math.hypot(...вправо) < 1e-6) вправо = вект([0, 0, 1], n);
  вправо = норм(вправо);
  return { нормаль: n, вправо, вверх: норм(вект(n, вправо)) };
}

/** Значение ближайшего вокселя в точке мира (мм); вне тома — null. */
export function значениеВТочке(данные, мир) {
  const [nx, ny, nz] = данные.dims;
  const ид = мирВИндекс(данные, мир);
  const i = Math.round(ид[0]), j = Math.round(ид[1]), k = Math.round(ид[2]);
  if (i < 0 || j < 0 || k < 0 || i >= nx || j >= ny || k >= nz) return null;
  const v = читать(данные, (k * ny + j) * nx + i);
  return Number.isFinite(v) ? v : null;
}

/**
 * Проба (2·полуN+1)² точек вокруг центра В ПЛОСКОСТИ ВИДА с шагом в мм.
 * Точки вне тома пропускаются. Шаг по умолчанию — самый мелкий шаг тома:
 * «7×7 вокселей» остаются семью вокселями и в боковом окне, где по одной
 * оси шаг 0,7 мм, а по другой — толщина среза.
 */
export function пробаВПлоскости(данные, центр, оси, полуN = 3, шаг = null) {
  const ш = шаг || Math.min(...(данные.spacing || [1, 1, 1]).filter((s) => s > 0));
  const out = [];
  for (let b = -полуN; b <= полуN; b++) for (let a = -полуN; a <= полуN; a++) {
    const p = [0, 1, 2].map((r) => центр[r] + (a * оси.вправо[r] + b * оси.вверх[r]) * ш);
    const v = значениеВТочке(данные, p);
    if (v != null) out.push(v);
  }
  return out;
}

export function отсортировать(значения) {
  const a = Float64Array.from(значения);
  a.sort();
  return a;
}

/** Перцентиль отсортированного массива (0…100), ближайший ранг. */
export function перцентиль(отсорт, p) {
  if (!отсорт.length) return NaN;
  const i = Math.min(отсорт.length - 1, Math.max(0, Math.round((p / 100) * (отсорт.length - 1))));
  return отсорт[i];
}

export function медиана(значения) {
  const s = отсортировать(значения);
  if (!s.length) return NaN;
  const h = s.length >> 1;
  return s.length % 2 ? s[h] : (s[h - 1] + s[h]) / 2;
}

/** Мин, макс, среднее, СКО (по совокупности). Пусто — null. */
export function статистика(значения) {
  let n = 0, мин = Infinity, макс = -Infinity, сум = 0, кв = 0;
  for (const v of значения) {
    if (!Number.isFinite(v)) continue;
    n++; сум += v; кв += v * v;
    if (v < мин) мин = v; if (v > макс) макс = v;
  }
  if (!n) return null;
  const среднее = сум / n;
  return { n, мин, макс, среднее, ско: Math.sqrt(Math.max(0, кв / n - среднее * среднее)) };
}

/** Гистограмма в корзин корзин между мин и макс включительно. */
export function гистограмма(значения, корзин = 256, мин = null, макс = null) {
  const с = (мин == null || макс == null) ? статистика(значения) : { мин, макс };
  const h = new Uint32Array(корзин);
  if (!с) return { корзины: h, мин: NaN, макс: NaN };
  const размах = (с.макс - с.мин) || 1;
  for (const v of значения) {
    if (!Number.isFinite(v)) continue;
    h[Math.min(корзин - 1, Math.max(0, Math.floor(((v - с.мин) / размах) * корзин)))]++;
  }
  return { корзины: h, мин: с.мин, макс: с.макс };
}

/**
 * Класс ткани по медиане пробы (HU) и окно этого класса. Границы —
 * соглашения радиологии по шкале Хаунсфилда, а не подгонка: воздух в
 * лёгком глубоко отрицателен (≤ −300), губчатая кость начинается за +200.
 * Числа окон — те же, что стояли здесь с 10.19.30: лёгочное 1500/−600,
 * костное 1800/400, мягкие ткани — ширина 350 с центром по самой пробе.
 */
export function тканьПоHU(м) {
  if (!Number.isFinite(м)) return null;
  if (м <= -300) return { ткань: 'лёгкое', ww: 1500, wc: -600 };
  if (м >= 200) return { ткань: 'кость', ww: 1800, wc: 400 };
  return { ткань: 'мягкие ткани', ww: 350, wc: Math.round(м) };
}

/** Окно по области: 5-й и 95-й перцентили — края, середина — уровень. */
export function окноПоОбласти(значения) {
  const s = отсортировать(значения.filter((v) => Number.isFinite(v)));
  if (s.length < 2) return null;
  const низ = перцентиль(s, 5), верх = перцентиль(s, 95);
  const ww = Math.max(1, Math.round(верх - низ));
  return { ww, wc: Math.round((верх + низ) / 2), низ, верх, n: s.length };
}

/**
 * Срез, который сейчас на экране: плоскость {точка, нормаль, верх}.
 *
 * Если нормаль идёт вдоль оси тома (обычные аксиальный, боковой, фронтальный
 * виды и снимок стопки) — берётся РОВНО плоскость вокселей, без
 * интерполяции. Косой вид — сетка в плоскости с шагом в самый мелкий шаг
 * тома, по ближайшему вокселю; точки вне тома — NaN.
 *
 * @returns {{значения:Float32Array, ширина:number, высота:number,
 *   мир:(a:number,b:number)=>number[], ровно:boolean, номер?:number, всего?:number}|null}
 */
export function срезВида(данные, плоскость) {
  const [nx, ny, nz] = данные.dims;
  const d = D(данные);
  const осиТома = [[d[0], d[1], d[2]], [d[3], d[4], d[5]], [d[6], d[7], d[8]]];
  const n = норм(плоскость.нормаль);
  const t = осиТома.findIndex((о) => Math.abs(скал(о, n)) > 0.999);
  const размеры = [nx, ny, nz];
  if (t >= 0) {
    const ид = мирВИндекс(данные, плоскость.точка);
    const номер = Math.round(ид[t]);
    if (номер < 0 || номер >= размеры[t]) return null;
    const [ta, tb] = [0, 1, 2].filter((x) => x !== t);
    const W = размеры[ta], H = размеры[tb];
    const значения = new Float32Array(W * H);
    const ijk = [0, 0, 0];
    ijk[t] = номер;
    for (let b = 0; b < H; b++) for (let a = 0; a < W; a++) {
      ijk[ta] = a; ijk[tb] = b;
      значения[b * W + a] = читать(данные, (ijk[2] * ny + ijk[1]) * nx + ijk[0]);
    }
    const мир = (a, b) => { const q = [0, 0, 0]; q[t] = номер; q[ta] = a; q[tb] = b; return индексВМир(данные, q); };
    return { значения, ширина: W, высота: H, мир, ровно: true, номер, всего: размеры[t] };
  }
  // Косой вид: сетка вокруг проекции центра тома, покрывающая весь том.
  const оси = осиВида(n, плоскость.верх);
  const sp = данные.spacing || [1, 1, 1];
  const шаг = Math.min(...sp.filter((s) => s > 0));
  const центрТома = индексВМир(данные, [(nx - 1) / 2, (ny - 1) / 2, (nz - 1) / 2]);
  const r = [0, 1, 2].map((x) => центрТома[x] - плоскость.точка[x]);
  const вдоль = скал(r, n);
  const центр = [0, 1, 2].map((x) => центрТома[x] - вдоль * n[x]);
  const диаг = Math.hypot(nx * sp[0], ny * sp[1], nz * sp[2]);
  const N = Math.min(1024, Math.ceil(диаг / шаг) + 1);
  const значения = new Float32Array(N * N);
  const мир = (a, b) => [0, 1, 2].map((x) => центр[x] + ((a - (N - 1) / 2) * оси.вправо[x] + ((N - 1) / 2 - b) * оси.вверх[x]) * шаг);
  for (let b = 0; b < N; b++) for (let a = 0; a < N; a++) {
    const v = значениеВТочке(данные, мир(a, b));
    значения[b * N + a] = v == null ? NaN : v;
  }
  return { значения, ширина: N, высота: N, мир, ровно: false };
}

/**
 * Фигура ROI в координатах плоскости (мм от точки плоскости по осям вида).
 * Точки — handles.points аннотации Cornerstone в мире:
 *   RectangleROI — четыре угла; EllipticalROI — [низ, верх, лево, право];
 *   CircleROI — [центр, точка на окружности].
 */
export function фигураВПлоскости(тип, точки, плоскость) {
  const оси = осиВида(плоскость.нормаль, плоскость.верх);
  const uv = (p) => { const r = [0, 1, 2].map((x) => p[x] - плоскость.точка[x]); return [скал(r, оси.вправо), скал(r, оси.вверх)]; };
  const п = (точки || []).map(uv);
  if (/Rectangle/i.test(тип) && п.length >= 2) {
    const us = п.map((q) => q[0]), vs = п.map((q) => q[1]);
    return { тип: 'прямоугольник', u0: Math.min(...us), u1: Math.max(...us), v0: Math.min(...vs), v1: Math.max(...vs), оси };
  }
  if (/Elliptical/i.test(тип) && п.length >= 4) {
    const [низ, верх, лево, право] = п;
    const ц = [(низ[0] + верх[0] + лево[0] + право[0]) / 4, (низ[1] + верх[1] + лево[1] + право[1]) / 4];
    const e1 = [верх[0] - низ[0], верх[1] - низ[1]], e2 = [право[0] - лево[0], право[1] - лево[1]];
    const a = Math.hypot(...e1) / 2, b = Math.hypot(...e2) / 2;
    return { тип: 'эллипс', ц, e1: [e1[0] / (2 * a || 1), e1[1] / (2 * a || 1)], e2: [e2[0] / (2 * b || 1), e2[1] / (2 * b || 1)], a, b, оси };
  }
  if (/Circle/i.test(тип) && п.length >= 2) {
    return { тип: 'круг', ц: п[0], r: Math.hypot(п[1][0] - п[0][0], п[1][1] - п[0][1]), оси };
  }
  return null;
}

/** Лежит ли точка мира внутри фигуры (в той же плоскости). */
export function вФигуре(ф, мир, плоскость) {
  const r = [0, 1, 2].map((x) => мир[x] - плоскость.точка[x]);
  const u = скал(r, ф.оси.вправо), v = скал(r, ф.оси.вверх);
  if (ф.тип === 'прямоугольник') return u >= ф.u0 && u <= ф.u1 && v >= ф.v0 && v <= ф.v1;
  if (ф.тип === 'круг') return Math.hypot(u - ф.ц[0], v - ф.ц[1]) <= ф.r;
  if (ф.тип === 'эллипс') {
    const du = u - ф.ц[0], dv = v - ф.ц[1];
    const p = du * ф.e1[0] + dv * ф.e1[1], q = du * ф.e2[0] + dv * ф.e2[1];
    return (p / (ф.a || 1e-9)) ** 2 + (q / (ф.b || 1e-9)) ** 2 <= 1;
  }
  return false;
}

/** Все точки фигуры лежат в плоскости среза (допуск — мм вдоль нормали). */
export function наПлоскости(точки, плоскость, допуск) {
  const n = норм(плоскость.нормаль);
  return (точки || []).length > 0 && точки.every((p) => Math.abs(скал([0, 1, 2].map((x) => p[x] - плоскость.точка[x]), n)) <= допуск);
}

/**
 * Вычитание «контраст − маска» и MIP разности во ФРОНТАЛЬНОЙ проекции — так
 * выглядит субтракционная ангиограмма. Разность в отдельный массив не
 * кладётся: на 512×512×800 это был бы ещё один том в 840 МБ.
 *
 * Проекция идёт вдоль оси тома, ближе всех к передне-задней (Y мира, LPS).
 * Картинка — как принято у рентгенологов: голова сверху, правая сторона
 * пациента слева (+X мира — левая сторона пациента — уходит вправо).
 *
 * @returns {{data:Float32Array, width:number, height:number, мин:number, макс:number, шагГор:number, шагВерт:number}}
 */
export function мипРазности(контраст, маска, dims, direction, усиление = 1, spacing = [1, 1, 1]) {
  const d = direction && direction.length === 9 ? direction : [1, 0, 0, 0, 1, 0, 0, 0, 1];
  const оси = [[d[0], d[1], d[2]], [d[3], d[4], d[5]], [d[6], d[7], d[8]]];
  const лучшая = (комп, кроме = []) => [0, 1, 2].filter((a) => !кроме.includes(a)).sort((a, b) => Math.abs(оси[b][комп]) - Math.abs(оси[a][комп]))[0];
  const тЛуч = лучшая(1);              // вдоль Y мира — туда смотрим
  const тГор = лучшая(0, [тЛуч]);      // вдоль X мира — по горизонтали
  const тВерт = [0, 1, 2].find((a) => a !== тЛуч && a !== тГор);
  const шаги = [1, dims[0], dims[0] * dims[1]];
  const W = dims[тГор], H = dims[тВерт], L = dims[тЛуч];
  const слеваНаправо = оси[тГор][0] >= 0;  // рост индекса — к левой стороне пациента
  const головаВверх = оси[тВерт][2] >= 0;  // рост индекса — к голове
  const data = new Float32Array(W * H);
  let мин = Infinity, макс = -Infinity;
  for (let v = 0; v < H; v++) for (let h = 0; h < W; h++) {
    let м = -Infinity;
    let i = h * шаги[тГор] + v * шаги[тВерт];
    for (let l = 0; l < L; l++, i += шаги[тЛуч]) {
      const р = (контраст[i] - маска[i]) * усиление;
      if (р > м) м = р;
    }
    const x = слеваНаправо ? h : W - 1 - h;
    const y = головаВверх ? H - 1 - v : v;
    data[y * W + x] = м;
    if (м < мин) мин = м; if (м > макс) макс = м;
  }
  return { data, width: W, height: H, мин, макс, шагГор: spacing[тГор] || 1, шагВерт: spacing[тВерт] || 1 };
}

/** Два тома лежат в одной сетке: размеры, шаг, начало и оси совпадают. */
export function геометрияСовпадает(a, b) {
  if (!a || !b) return { ok: false, причина: 'нет данных тома' };
  if (a.dims.some((v, i) => v !== b.dims[i])) return { ok: false, причина: `размеры не совпадают — ${a.dims.join('×')} и ${b.dims.join('×')}` };
  const мш = Math.min(...a.spacing);
  if (a.spacing.some((v, i) => Math.abs(v - b.spacing[i]) > 1e-3 * Math.max(1, v))) return { ok: false, причина: `шаг вокселя разный — ${a.spacing.map((v) => +v.toFixed(2)).join('×')} и ${b.spacing.map((v) => +v.toFixed(2)).join('×')} мм` };
  const сдвиг = Math.hypot(a.origin[0] - b.origin[0], a.origin[1] - b.origin[1], a.origin[2] - b.origin[2]);
  if (сдвиг > мш / 2) return { ok: false, причина: `тома сдвинуты друг относительно друга на ${сдвиг.toFixed(1)} мм` };
  if (D(a).some((v, i) => Math.abs(v - D(b)[i]) > 1e-3)) return { ok: false, причина: 'оси томов повёрнуты друг относительно друга' };
  return { ok: true };
}

/**
 * Ролик 2D-окна: один проход по всей серии. Длинную серию прореживаем, чтобы
 * ролик не шёл дольше предела (по умолчанию 30 с): 800 срезов при 15 кадрах/с
 * — это 53 с, с шагом 2 — 27 с.
 */
export function планРолика(всего, кадровВСек = 15, пределСек = 30) {
  const n = Math.max(1, Math.floor(всего) || 1);
  const шаг = Math.max(1, Math.ceil(n / (кадровВСек * пределСек)));
  const индексы = [];
  for (let i = 0; i < n; i += шаг) индексы.push(i);
  if (индексы[индексы.length - 1] !== n - 1) индексы.push(n - 1);
  return { шаг, индексы, кадров: индексы.length, секунд: индексы.length / кадровВСек };
}

/**
 * Масштаб сетки: сколько пикселей экрана в миллиметре. У окон Cornerstone —
 * и стопки, и реформата — parallelScale есть половина высоты окна в мм мира.
 * Прежняя формула для стопки (увеличение / шаг пикселя) не знала размера
 * окна: при снимке 2,5 мм/пиксель шаг сетки выходил 4 пикселя, и сетка
 * не рисовалась вовсе.
 */
export function масштабСетки(высотаПикс, parallelScale, шагМм = 10) {
  if (!(высотаПикс > 0) || !(parallelScale > 0)) return null;
  const пиксНаМм = (высотаПикс / 2) / parallelScale;
  return { пиксНаМм, шагПикс: шагМм * пиксНаМм, полоса50: 50 * пиксНаМм };
}

/**
 * Соседняя закладка по номеру среза: «вперёд» — ближайшая выше текущего,
 * «назад» — ближайшая ниже; за краем — по кругу. Закладок нет — null.
 */
export function соседняяЗакладка(номера, текущий, направление = 'next') {
  const s = [...new Set((номера || []).filter((v) => Number.isFinite(v)))].sort((a, b) => a - b);
  if (!s.length) return null;
  if (направление === 'next') return s.find((v) => v > текущий) ?? s[0];
  return [...s].reverse().find((v) => v < текущий) ?? s[s.length - 1];
}

export default {
  осиВида, значениеВТочке, пробаВПлоскости, отсортировать, перцентиль, медиана, статистика, гистограмма,
  тканьПоHU, окноПоОбласти, срезВида, фигураВПлоскости, вФигуре, наПлоскости, мипРазности,
  геометрияСовпадает, планРолика, масштабСетки, соседняяЗакладка,
};
